import { useMutation } from '@apollo/client';
import {
  DefaultButton,
  IconButton,
  Modal,
  PrimaryButton,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import { CloseButton } from 'common/components/Buttons';
import { cutToast } from 'utility/toast';
import { useCommonStyles } from 'common/styles';
import { useState } from 'react';
import { ApprovalTestGroups_approvalTestGroups_nodes } from '../__generated__/ApprovalTestGroups';
import {
  ApprovalTestGroupCreate,
  ApprovalTestGroupCreateVariables,
} from './__generated__/ApprovalTestGroupCreate';
import {
  ApprovalTestGroupUpdate,
  ApprovalTestGroupUpdateVariables,
} from './__generated__/ApprovalTestGroupUpdate';
import APPROVAL_TEST_GROUP_CREATE from './ApprovalTestGroupCreate.graphql';
import APPROVAL_TEST_GROUP_EDIT from './ApprovalTestGroupUpdate.graphql';
import { useStyles } from './index.styles';
import APPROVAL_TEST_RESULTS from '../../../ApprovalTestResults.graphql';
import { useLocation } from 'react-router-dom';

interface AddGroupModalProps {
  isEdit?: boolean;
  selectedGroup?: ApprovalTestGroups_approvalTestGroups_nodes | undefined;
}

export const AddGroupModal: React.FC<AddGroupModalProps> = ({
  isEdit,
  selectedGroup,
}) => {
  const styles = useStyles();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const approvalId = queryParams.get('id');
  const [name, setName] = useState<string | undefined>(
    isEdit ? selectedGroup?.name : undefined
  );
  const [visible, setVisible] = useState<boolean>(false);

  const commonStyles = useCommonStyles();

  const [addTestGroup] = useMutation<
    ApprovalTestGroupCreate,
    ApprovalTestGroupCreateVariables
  >(APPROVAL_TEST_GROUP_CREATE);

  const [editTestGroup] = useMutation<
    ApprovalTestGroupUpdate,
    ApprovalTestGroupUpdateVariables
  >(APPROVAL_TEST_GROUP_EDIT);

  const addGroup = async () => {
    if (!isEdit) {
      const variables = {
        input: {
          approvalTestGroup: {
            name: name ?? '',
          },
        },
      };
      const { errors, data } = await addTestGroup({ variables });

      if (errors?.length) cutToast.error(errors[0].message);
      else {
        cutToast.success('Test group added successfully');
        setVisible(false);
      }
    } else {
      const variables = {
        input: {
          id: selectedGroup?.id!,
          rowTimestamp: selectedGroup?._rowTimestamp!,
          approvalTestGroupPatch: {
            name: name,
          },
        },
      };
      const { errors } = await editTestGroup({
        variables,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: APPROVAL_TEST_RESULTS,
            variables: {
              filter: {
                approvalId: { equalTo: approvalId! },
              },
              orderBy: '_FULL_COMMENT_ASC',
            },
          },
        ],
      });

      if (errors?.length) cutToast.error(errors[0].message);
      else {
        cutToast.success('Test group edited successfully');
        setVisible(false);
      }
    }
  };

  return (
    <>
      <IconButton
        onClick={() => setVisible(true)}
        iconProps={{ iconName: isEdit ? 'Edit' : 'FabricNewFolder' }}
        className={styles.iconStyle}
        ariaLabel="Collapse"
      />
      {visible && (
        <Modal isOpen isBlocking>
          <Stack
            tokens={{
              padding: 20,
              childrenGap: 30,
            }}
          >
            <Stack
              horizontal
              // className={styles.container}
              horizontalAlign="space-between"
              verticalAlign="center"
            >
              <Stack tokens={{ childrenGap: 10 }}>
                <Text
                  variant="xLarge"
                  className={commonStyles.colorThemePrimary}
                >
                  {isEdit ? 'Edit Group' : 'Add Group'}
                </Text>
              </Stack>
              <CloseButton onClick={() => setVisible(false)} />
            </Stack>
            <TextField
              label="Name"
              placeholder="Group Name"
              value={name}
              resizable={false}
              onChange={(_event, value) => {
                setName(value);
              }}
            />
            <Stack
              horizontal
              horizontalAlign="end"
              tokens={{ childrenGap: 20 }}
            >
              <PrimaryButton
                text="Save"
                onClick={addGroup}
                disabled={!name || name === selectedGroup?.name}
              />
              <DefaultButton onClick={() => setVisible(false)} text="Cancel" />
            </Stack>
          </Stack>
        </Modal>
      )}
    </>
  );
};
